import { zodResolver } from '@hookform/resolvers/zod';
import BrushRounded from '@mui/icons-material/BrushRounded';
import StorefrontRounded from '@mui/icons-material/StorefrontRounded';
import { Box, Stack, TextField, Typography } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { ImageEditor, ImageFill } from '../../../components/images';
import { initials } from '../../../lib/format';
import { useNotify } from '../../../lib/notify';
import { useSaveSettings, type ShopSettings } from '../api';
import { applyFieldErrors } from '../lib';
import { FieldGrid, SaveBar, SectionCard } from '../ui';
import { ThemeColorField, ThemePreview } from './ThemeColorField';

const optional = (max: number) =>
  z
    .string()
    .trim()
    .max(max, `At most ${String(max)} characters`);

export const brandingSchema = z.object({
  name: z.string().trim().min(1, 'Enter the shop name').max(200),
  phone: optional(40),
  whatsapp: optional(40),
  email: z.union([z.literal(''), z.email('Enter a valid email').max(254)]),
  address: optional(500),
  themeColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'Use a 6-digit hex colour like #05473E'),
});
type Values = z.infer<typeof brandingSchema>;

const FIELDS = ['name', 'phone', 'whatsapp', 'email', 'address', 'themeColor'] as const;

function toValues(s: ShopSettings): Values {
  return {
    name: s.name,
    phone: s.phone ?? '',
    whatsapp: s.whatsapp ?? '',
    email: s.email ?? '',
    address: s.address ?? '',
    themeColor: s.themeColor.toUpperCase(),
  };
}

const orNull = (v: string) => (v.trim() ? v.trim() : null);

export function BrandingTab({
  settings,
  canManage,
}: {
  settings: ShopSettings;
  canManage: boolean;
}) {
  const notify = useNotify();
  const save = useSaveSettings();
  const form = useForm<Values>({
    resolver: zodResolver(brandingSchema),
    values: toValues(settings),
    resetOptions: { keepDirtyValues: false },
  });
  const {
    register,
    control,
    watch,
    reset,
    setError,
    formState: { errors, isDirty },
  } = form;

  const onSubmit = form.handleSubmit(async (v) => {
    try {
      await save.mutateAsync({
        name: v.name.trim(),
        phone: orNull(v.phone),
        whatsapp: orNull(v.whatsapp),
        email: orNull(v.email),
        address: orNull(v.address),
        themeColor: v.themeColor.toUpperCase(),
      });
      notify.success('Shop profile saved');
    } catch (e) {
      if (!applyFieldErrors(e, FIELDS, setError)) notify.error(e);
    }
  });

  const name = watch('name');
  const color = watch('themeColor');
  const ro = !canManage;

  return (
    <form onSubmit={(e) => void onSubmit(e)} noValidate>
      <Stack spacing={2.5}>
        <SectionCard
          icon={<StorefrontRounded />}
          title="Shop profile"
          description="Shown on invoices, receipts and statements your customers receive."
        >
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
            <Stack spacing={1} sx={{ alignItems: 'center', width: { md: 132 }, flexShrink: 0 }}>
              <ImageEditor
                target={{ kind: 'logo' }}
                url={settings.logoUrl}
                canEdit={!ro}
                label="your shop"
              >
                <Box
                  role="img"
                  aria-label="Shop logo"
                  sx={(t) => {
                    const p = (t.vars ?? t).palette.primary;
                    return {
                      width: 112,
                      height: 112,
                      borderRadius: '28px',
                      overflow: 'hidden',
                      display: 'grid',
                      placeItems: 'center',
                      color: '#fff',
                      fontSize: 38,
                      fontWeight: 750,
                      letterSpacing: '-0.02em',
                      ...(settings.logoUrl
                        ? { bgcolor: '#fff', border: 1, borderColor: 'divider' }
                        : {
                            background: `linear-gradient(135deg, ${p.light}, ${p.main} 55%, ${p.dark})`,
                            boxShadow: `0 14px 28px -12px ${p.main}, inset 0 1px 0 rgba(255,255,255,.25)`,
                          }),
                    };
                  }}
                >
                  <ImageFill
                    url={settings.logoUrl}
                    fit="contain"
                    fallback={initials(name || settings.name)}
                  />
                </Box>
              </ImageEditor>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ textAlign: 'center', maxWidth: 160 }}
              >
                {settings.logoUrl
                  ? 'Printed on invoices, receipts and reports.'
                  : 'Add your logo (JPEG, PNG or WebP, up to 5 MB). Your initials are used until then.'}
              </Typography>
            </Stack>
            <Stack spacing={2} sx={{ flex: 1, minWidth: 0 }}>
              <TextField
                label="Shop name"
                disabled={ro}
                {...register('name')}
                error={Boolean(errors.name)}
                helperText={errors.name?.message}
              />
              <FieldGrid>
                <TextField
                  label="Phone"
                  autoComplete="tel"
                  disabled={ro}
                  {...register('phone')}
                  error={Boolean(errors.phone)}
                  helperText={errors.phone?.message}
                />
                <TextField
                  label="WhatsApp"
                  disabled={ro}
                  {...register('whatsapp')}
                  error={Boolean(errors.whatsapp)}
                  helperText={errors.whatsapp?.message}
                />
              </FieldGrid>
              <TextField
                label="Email"
                type="email"
                disabled={ro}
                {...register('email')}
                error={Boolean(errors.email)}
                helperText={errors.email?.message}
              />
              <TextField
                label="Address"
                multiline
                minRows={2}
                disabled={ro}
                {...register('address')}
                error={Boolean(errors.address)}
                helperText={errors.address?.message}
              />
            </Stack>
          </Stack>
        </SectionCard>

        <SectionCard
          icon={<BrushRounded />}
          title="Brand colour"
          description="Tints buttons, links and highlights across the app for everyone in your shop."
        >
          <Box
            sx={{
              display: 'grid',
              gap: 3,
              gridTemplateColumns: { xs: 'minmax(0,1fr)', md: 'minmax(0,1fr) minmax(0,1.1fr)' },
              alignItems: 'start',
            }}
          >
            <Controller
              control={control}
              name="themeColor"
              render={({ field }) => (
                <ThemeColorField
                  value={field.value}
                  onChange={(hex) => field.onChange(hex)}
                  error={errors.themeColor?.message}
                  disabled={ro}
                />
              )}
            />
            <Box>
              <Typography variant="overline" color="text.secondary">
                Live preview
              </Typography>
              <ThemePreview hex={color} shopName={name} />
            </Box>
          </Box>
        </SectionCard>
      </Stack>
      <SaveBar
        dirty={isDirty}
        saving={save.isPending}
        disabled={ro}
        onReset={() => reset(toValues(settings))}
      />
    </form>
  );
}
